'use client';

import { useEffect, useRef, useState } from 'react';
import NextImage from 'next/image';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import type { Swiper as SwiperInstance } from 'swiper';
import { FreeMode, Mousewheel, Thumbs } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/free-mode';
import 'swiper/css/thumbs';
import ImageSkeleton from 'components/commercetools-ui/atoms/image-skeleton';
import LoadingImage from 'components/commercetools-ui/atoms/loading-image';
import useImageSizes from 'helpers/hooks/useImageSizes';
import { DEFAULT_PHOTO_HEIGHT, DEFAULT_PHOTO_WIDTH, MyPhotoItem } from 'helpers/hooks/useMyPhotos';
import { buildRetrySrc } from 'helpers/imageData';

const MAX_AUTOMATIC_RETRIES = 1;
const RETRY_BASE_DELAY_MS = 500;

type PhotoLoadFailure = { attempts: number; isExhausted: boolean };

export type WidgetPreviewContentProps = {
  photos: MyPhotoItem[];
  activePhotoId: string;
  onSelectPhoto: (photoId: string) => void;
  previewErrorMessage?: string;
  previousPhotoLabel?: string;
  nextPhotoLabel?: string;
  enableSwipe?: boolean;
};

const WidgetPreviewContent = ({
  photos,
  activePhotoId,
  onSelectPhoto,
  previewErrorMessage,
  previousPhotoLabel,
  nextPhotoLabel,
  enableSwipe = true,
}: WidgetPreviewContentProps) => {
  const hasMultiplePhotos = photos.length > 1;
  const previewImageSizes = useImageSizes({ sm: 1, md: 1, lg: 1, defaultSize: 1, maxWidth: 980 });

  const mainSwiperRef = useRef<SwiperInstance | null>(null);
  const [thumbsSwiper, setThumbsSwiper] = useState<SwiperInstance | null>(null);

  const [isTransitioning, setIsTransitioning] = useState(false);
  const [failuresByPhotoId, setFailuresByPhotoId] = useState<Record<string, PhotoLoadFailure>>({});
  const [everActivePhotoIds, setEverActivePhotoIds] = useState<Set<string>>(() => new Set());
  const [loadedPhotoIds, setLoadedPhotoIds] = useState<Set<string>>(() => new Set());
  const retryTimeoutsRef = useRef<Record<string, number>>({});

  /**
   * The active photo is identified by id, never by a stored index: `photos` can shrink under us
   * (a removal from the preview) and a retained numeric index would then point at a different photo.
   */
  const activeIndex = Math.max(
    photos.findIndex((photo) => photo.id === activePhotoId),
    0,
  );
  /**
   * `initialSlide` is read once at init but is in swiper-react's watched param list, so feeding it a
   * live value makes every selection run `updateSwiper` -> `swiper.update()` -> an instant
   * `slideTo(activeIndex)` that cuts off whatever transition is mid-flight. Pin it to the mount
   * value; the sync effect below is what keeps position correct afterwards.
   */
  const initialSlideRef = useRef(activeIndex);

  const activePhoto = photos[activeIndex];
  const hasPrevious = activeIndex > 0;
  const hasNext = activeIndex < photos.length - 1;

  useEffect(() => {
    const swiper = mainSwiperRef.current;
    if (!swiper || swiper.destroyed) return;

    swiper.update?.();
    if (swiper.activeIndex !== activeIndex) {
      swiper.slideTo(activeIndex, 0);
    }
  }, [activeIndex, photos]);

  /**
   * Swiper's Thumbs module scrolls the active thumb into view but only left-aligns it, and counts a
   * one-pixel sliver at the edge as "visible" — so the selected thumb can sit clipped. We centre it.
   *
   * This only ever moves the strip. It must not call `updateActiveIndex`/`updateSlidesClasses`:
   * rewriting Swiper's own active bookkeeping is what desynced the filmstrip from the preview.
   */
  const hasScrolledThumbsRef = useRef(false);

  useEffect(() => {
    const thumbs = thumbsSwiper;
    if (!thumbs || thumbs.destroyed) return;

    const slide = thumbs.slides?.[activeIndex];
    if (!slide) return;

    if (!thumbs.slidesSizesGrid?.length) thumbs.update();

    const slideOffset = slide.swiperSlideOffset ?? slide.offsetLeft;
    const slideSize = thumbs.slidesSizesGrid?.[activeIndex] ?? slide.offsetWidth;
    const centeredTranslate = -(slideOffset - (thumbs.size - slideSize) / 2);
    const clampedTranslate = Math.max(thumbs.maxTranslate(), Math.min(thumbs.minTranslate(), centeredTranslate));

    const speed = hasScrolledThumbsRef.current ? 300 : 0;
    hasScrolledThumbsRef.current = true;

    if (Math.abs(thumbs.translate - clampedTranslate) < 1) return;

    thumbs.translateTo(clampedTranslate, speed);
  }, [thumbsSwiper, activeIndex, photos.length]);

  useEffect(() => {
    setEverActivePhotoIds((prev) => (prev.has(activePhotoId) ? prev : new Set(prev).add(activePhotoId)));
  }, [activePhotoId]);

  useEffect(() => {
    const timeouts = retryTimeoutsRef.current;
    return () => {
      Object.values(timeouts).forEach((timeoutId) => window.clearTimeout(timeoutId));
    };
  }, []);

  /**
   * Single upward sync: only a real position change is published to the parent. Without this guard
   * the echo of our own `slideTo` re-enters the parent and ping-pongs the preview between slides.
   */
  const handleSlideChange = (swiper: SwiperInstance) => {
    const photo = photos[swiper.activeIndex];
    if (!photo || photo.id === activePhotoId) return;
    onSelectPhoto(photo.id);
  };

  if (!activePhoto) return null;

  const goToPrevious = () => {
    if (!hasPrevious || isTransitioning) return;
    mainSwiperRef.current?.slidePrev();
  };

  const goToNext = () => {
    if (!hasNext || isTransitioning) return;
    mainSwiperRef.current?.slideNext();
  };

  const handleImageError = (photoId: string) => {
    if (retryTimeoutsRef.current[photoId]) return;

    const attempts = failuresByPhotoId[photoId]?.attempts ?? 0;
    if (attempts >= MAX_AUTOMATIC_RETRIES) {
      setFailuresByPhotoId((prev) => ({ ...prev, [photoId]: { attempts, isExhausted: true } }));
      return;
    }

    retryTimeoutsRef.current[photoId] = window.setTimeout(
      () => {
        delete retryTimeoutsRef.current[photoId];
        setFailuresByPhotoId((prev) => ({
          ...prev,
          [photoId]: { attempts: (prev[photoId]?.attempts ?? 0) + 1, isExhausted: false },
        }));
        // The retry remounts the <img>, so the slide is back in flight and waits again.
        setLoadedPhotoIds((prev) => {
          if (!prev.has(photoId)) return prev;
          const next = new Set(prev);
          next.delete(photoId);
          return next;
        });
      },
      RETRY_BASE_DELAY_MS * 2 ** attempts,
    );
  };

  const handleImageLoaded = (photoId: string) => {
    setLoadedPhotoIds((prev) => (prev.has(photoId) ? prev : new Set(prev).add(photoId)));
  };

  const getIndexDistanceFromActive = (index: number) => Math.abs(index - activeIndex);

  return (
    <div className="flex h-full flex-col">
      <div className="flex min-h-0 flex-1 items-center justify-center overflow-hidden px-8">
        <Swiper
          modules={[Thumbs]}
          thumbs={{ swiper: thumbsSwiper }}
          initialSlide={initialSlideRef.current}
          slidesPerView={1}
          spaceBetween={24}
          allowTouchMove={hasMultiplePhotos && enableSwipe}
          onSwiper={(swiper) => {
            mainSwiperRef.current = swiper;
          }}
          onSlideChange={handleSlideChange}
          onSlideChangeTransitionStart={() => setIsTransitioning(true)}
          onSlideChangeTransitionEnd={() => setIsTransitioning(false)}
          className="mx-auto size-full max-w-[920px] overflow-hidden rounded-lg"
          data-ffid="ctr-my-photos-widget-preview-main"
        >
          {photos.map((photo, index) => {
            const distanceFromActive = getIndexDistanceFromActive(index);
            const isEager = photo.id === activePhotoId || everActivePhotoIds.has(photo.id);
            const previewFetchPriority: 'high' | 'auto' = distanceFromActive === 0 ? 'high' : 'auto';
            const failure = failuresByPhotoId[photo.id];
            const attempts = failure?.attempts ?? 0;

            return (
              <SwiperSlide key={photo.id} className="!flex !h-full items-center justify-center">
                {failure?.isExhausted ? (
                  <div
                    className="flex min-h-220 items-center justify-center px-16 py-40 text-center text-14 text-[#5C5C5C]"
                    data-ffid={`msg-my-photos-widget-preview-error-${index + 1}`}
                  >
                    {previewErrorMessage || 'This photo could not be loaded.'}
                  </div>
                ) : (
                  <div
                    className="relative h-full max-w-full overflow-hidden rounded-lg"
                    style={{ aspectRatio: `${photo.width ?? DEFAULT_PHOTO_WIDTH} / ${photo.height ?? DEFAULT_PHOTO_HEIGHT}` }}
                  >
                    {!loadedPhotoIds.has(photo.id) && <ImageSkeleton />}
                    <NextImage
                      key={attempts}
                      src={buildRetrySrc(photo.previewSrc, attempts)}
                      alt={photo.alt}
                      width={photo.width ?? DEFAULT_PHOTO_WIDTH}
                      height={photo.height ?? DEFAULT_PHOTO_HEIGHT}
                      sizes={previewImageSizes}
                      unoptimized
                      loading={isEager ? 'eager' : 'lazy'}
                      fetchPriority={previewFetchPriority}
                      className="size-full object-cover"
                      onLoad={() => handleImageLoaded(photo.id)}
                      onError={() => handleImageError(photo.id)}
                      data-ffid={`img-my-photos-widget-preview-${index + 1}`}
                    />
                  </div>
                )}
              </SwiperSlide>
            );
          })}
        </Swiper>
      </div>

      {hasMultiplePhotos && (
        <div className="flex w-full flex-col items-center">
          <div
            className="mt-16 flex w-full max-w-[718px] items-center justify-center gap-16"
            data-ffid="ctr-my-photos-widget-preview-nav"
          >
            <button
              type="button"
              onClick={goToPrevious}
              disabled={!hasPrevious || isTransitioning}
              aria-label={previousPhotoLabel ?? 'Previous photo'}
              className="flex size-35 items-center justify-center rounded-full bg-white text-[#333333] shadow-[0px_3.5px_3.5px_0px_#00000040] transition hover:bg-[#F5F5F5] disabled:cursor-not-allowed disabled:opacity-40"
              data-ffid="btn-my-photos-widget-preview-prev"
            >
              <ChevronLeftIcon className="size-[17.5px]" aria-hidden="true" />
            </button>
            <button
              type="button"
              onClick={goToNext}
              disabled={!hasNext || isTransitioning}
              aria-label={nextPhotoLabel ?? 'Next photo'}
              className="flex size-35 items-center justify-center rounded-full bg-white text-[#333333] shadow-[0px_3.5px_3.5px_0px_#00000040] transition hover:bg-[#F5F5F5] disabled:cursor-not-allowed disabled:opacity-40"
              data-ffid="btn-my-photos-widget-preview-next"
            >
              <ChevronRightIcon className="size-[17.5px]" aria-hidden="true" />
            </button>
          </div>

          <div className="mt-12 w-full max-w-[718px] overflow-hidden">
            <Swiper
              onSwiper={setThumbsSwiper}
              modules={[FreeMode, Mousewheel, Thumbs]}
              freeMode
              grabCursor
              mousewheel={{ forceToAxis: true }}
              slidesPerView="auto"
              spaceBetween={8}
              centerInsufficientSlides
              className="w-full !px-2"
              data-ffid="ctr-my-photos-widget-preview-thumbnails"
              initialSlide={initialSlideRef.current}
            >
              {photos.map((photo, index) => {
                const isActive = photo.id === activePhoto.id;

                return (
                  <SwiperSlide
                    key={photo.id}
                    className={`!w-62 cursor-pointer overflow-hidden rounded-[5px] border-2 transition ${
                      isActive ? 'border-[#018463]' : 'border-transparent'
                    }`}
                    data-ffid={`btn-my-photos-widget-preview-thumb-${index + 1}`}
                  >
                    <button
                      type="button"
                      className="block size-62"
                      aria-label={photo.alt}
                      aria-current={isActive}
                      onClick={() => mainSwiperRef.current?.slideTo(index)}
                    >
                      <LoadingImage
                        src={photo.thumbnailSrc}
                        alt={photo.alt}
                        width={62}
                        height={62}
                        unoptimized
                        loading={isActive || everActivePhotoIds.has(photo.id) ? 'eager' : 'lazy'}
                        wrapperClassName="size-62"
                        className="size-62 object-cover"
                        data-ffid={`img-my-photos-widget-preview-thumb-${index + 1}`}
                      />
                    </button>
                  </SwiperSlide>
                );
              })}
            </Swiper>
          </div>
        </div>
      )}
    </div>
  );
};

export default WidgetPreviewContent;
